/**
 * Estado de la sesión.
 *
 * Al arrancar, la aplicación **intenta renovar la sesión** con la cookie
 * HttpOnly: si el usuario ya había entrado, vuelve a estar dentro sin tener que
 * escribir nada, y sin guardar ningún token en el navegador.
 */

import type { RegisterRequest, UserPublic } from '@eldescuadre/contracts';
import { create } from 'zustand';

import { setAccessToken, refreshSession } from './api';
import {
  acceptSession,
  fetchMe,
  login as loginRequest,
  logout as logoutRequest,
  register as registerRequest,
} from './auth';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

interface SessionState {
  user: UserPublic | null;
  status: SessionStatus;
  /** Modo privacidad: difumina los importes en pantalla. */
  stealth: boolean;
  error: string | null;

  init: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  /**
   * Aplica una sesión que ya viene resuelta.
   *
   * Lo usa la entrada con huella: el servidor devuelve exactamente lo mismo que con la
   * contraseña —token de acceso y usuario—, así que a partir de aquí la aplicación no
   * sabe ni le importa cómo se entró.
   */
  applySession: (session: { accessToken: string; user: UserPublic }) => void;
  signUp: (input: RegisterRequest) => Promise<void>;
  signOut: (allDevices?: boolean) => Promise<void>;
  toggleStealth: () => void;
  clearError: () => void;
}

const STEALTH_KEY = 'eldescuadre.stealth';

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  status: 'loading',
  stealth: localStorage.getItem(STEALTH_KEY) === 'true',
  error: null,

  init: async () => {
    set({ status: 'loading', error: null });
    try {
      const renewed = await refreshSession();
      if (!renewed) {
        set({ status: 'anonymous', user: null });
        return;
      }
      const user = await fetchMe();
      set({ status: 'authenticated', user });
    } catch {
      setAccessToken(null);
      set({ status: 'anonymous', user: null });
    }
  },

  signIn: async (email, password) => {
    set({ error: null });
    try {
      const result = await loginRequest({ email: email.trim().toLowerCase(), password });
      set({ status: 'authenticated', user: result.user });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'No se pudo iniciar sesión' });
      throw error;
    }
  },

  applySession: ({ accessToken, user }) => {
    acceptSession(accessToken);
    set({ status: 'authenticated', user, error: null });
  },

  signUp: async (input) => {
    set({ error: null });
    try {
      const result = await registerRequest(input);
      set({ status: 'authenticated', user: result.user });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'No se pudo crear la cuenta' });
      throw error;
    }
  },

  signOut: async (allDevices = false) => {
    try {
      await logoutRequest(allDevices);
    } finally {
      set({ status: 'anonymous', user: null });
    }
  },

  toggleStealth: () => {
    const next = !get().stealth;
    localStorage.setItem(STEALTH_KEY, String(next));
    set({ stealth: next });
  },

  clearError: () => set({ error: null }),
}));
